import { MUSIC_POLICY } from '../../music/replay/MusicPolicy';
import { MEDIA_KEYS, mediaAssetUrl } from '../content/MediaUrls';
import { validateGameSfxDefinition, type GameSfxDefinition, type OneShotAudioDefinition } from './GameSfxDefinition';

export interface MusicTrackDefinition {
  readonly id: string;
  readonly url: string;
  readonly dropSeconds: number;
  readonly durationSeconds: number;
  readonly gain: number;
}

export interface ReplayMusicTailDefinition {
  readonly durationSeconds: number;
  readonly transitionSeconds: number;
  readonly sustainSeconds: number;
  readonly gainScale: number;
  readonly wetMix: number;
  readonly lowPassHz: number;
  readonly wetLowPassHz: number;
  readonly fadeLowPassHz: number;
  readonly fadeWetLowPassHz: number;
  readonly highPassHz: number;
  readonly preDelaySeconds: number;
  readonly reverbSeconds: number;
  readonly reverbDecay: number;
}

export interface ReplayMusicDefinition {
  readonly minimumPreDropSeconds: number;
  readonly minimumPostDropSeconds: number;
  readonly fadeInSeconds: number;
  readonly tail: ReplayMusicTailDefinition;
}

export interface MatchAudioDefinition {
  readonly replayMusic: ReplayMusicDefinition;
  readonly sfx: GameSfxDefinition;
}

const DEFAULT_REPLAY_MUSIC: ReplayMusicDefinition = Object.freeze({
  minimumPreDropSeconds: MUSIC_POLICY.minimumPreDropSeconds,
  minimumPostDropSeconds: MUSIC_POLICY.minimumPostDropSeconds,
  fadeInSeconds: 0.18,
  tail: Object.freeze({
    durationSeconds: 12.5,
    transitionSeconds: 0.48,
    sustainSeconds: 1,
    gainScale: 0.58,
    wetMix: 0.53,
    lowPassHz: 2_400,
    wetLowPassHz: 3_600,
    fadeLowPassHz: 1_050,
    fadeWetLowPassHz: 1_650,
    highPassHz: 90,
    preDelaySeconds: 0.052,
    reverbSeconds: 3,
    reverbDecay: 1.95,
  }),
});

export function defaultMatchAudioDefinition(): MatchAudioDefinition {
  return Object.freeze({ replayMusic: DEFAULT_REPLAY_MUSIC, sfx: DEFAULT_GAME_SFX });
}

export function validateMatchAudioDefinition(definition: MatchAudioDefinition): void {
  validateReplayMusicDefinition(definition.replayMusic);
  validateGameSfxDefinition(definition.sfx);
}

const oneShot = (
  samples: readonly string[], gain: number, routing: 'global' | 'spatial' = 'spatial',
  gainByIntensity = 0, playbackRate = 1, playbackRateByIntensity = 0,
): OneShotAudioDefinition => Object.freeze({
  samples: Object.freeze([...samples]), routing, gain, gainByIntensity, playbackRate, playbackRateByIntensity,
});

const variants = (prefix: string, count: number): readonly string[] => Object.freeze(
  Array.from({ length: count }, (_, index) => mediaAssetUrl(`${prefix}.${String(index + 1).padStart(2, '0')}`)),
);

const DEFAULT_GAME_SFX: GameSfxDefinition = Object.freeze({
  engine: Object.freeze({
    loop: Object.freeze({ url: mediaAssetUrl(MEDIA_KEYS.engineIdle), routing: 'spatial' as const, gain: 1,
      fadeInSeconds: 0.03, fadeOutSeconds: 0.05 }),
    idleGain: 0.08, speedGain: 0.16, minPlaybackRate: 0.72, maxPlaybackRate: 2,
  }),
  boost: Object.freeze({
    start: oneShot([mediaAssetUrl(MEDIA_KEYS.boostStart)], 0.78),
    loop: Object.freeze({ url: mediaAssetUrl(MEDIA_KEYS.boostLoop), routing: 'spatial' as const, gain: 0.68,
      fadeInSeconds: 0.035, fadeOutSeconds: 0.045 }),
    release: oneShot([mediaAssetUrl(MEDIA_KEYS.boostRelease)], 0.7),
  }),
  boostPickupSmall: oneShot([mediaAssetUrl(MEDIA_KEYS.boostPickupSmall)], 1),
  boostPickupLarge: oneShot([mediaAssetUrl(MEDIA_KEYS.boostPickupLarge)], 1),
  jump: oneShot(variants('game.audio.sfx.jump', 4), 0.52),
  dodge: oneShot(variants('game.audio.sfx.dodge', 4), 0.58),
  doubleJump: oneShot(variants('game.audio.sfx.double-jump', 4), 0.54),
  wheelImpact: oneShot(variants('game.audio.sfx.wheel-impact', 4), 0.18, 'spatial', 0.3, 0.92, 0.16),
  ballImpact: oneShot(variants('game.audio.sfx.ball-impact', 6), 0.2, 'spatial', 0.34, 0.95, 0.12),
  carImpact: null,
  demolition: oneShot([mediaAssetUrl(MEDIA_KEYS.demolition)], 1),
  flipReset: oneShot([mediaAssetUrl(MEDIA_KEYS.flipReset)], 0.56),
  supersonic: Object.freeze({
    enter: oneShot(variants('game.audio.sfx.supersonic-enter', 3), Math.pow(10, -15 / 20)),
    loop: Object.freeze({ url: mediaAssetUrl(MEDIA_KEYS.supersonicLoop), routing: 'spatial' as const,
      gain: Math.pow(10, 6 / 20) * 0.045, fadeInSeconds: 0.2, fadeOutSeconds: 0.2,
      loopStartSeconds: 5802 / 24_000, loopEndSeconds: 106_283 / 24_000 }),
  }),
});

export function validateReplayMusicDefinition(definition: ReplayMusicDefinition): void {
  nonNegative(definition.minimumPreDropSeconds, 'Replay music minimum pre-drop duration');
  nonNegative(definition.minimumPostDropSeconds, 'Replay music minimum post-drop duration');
  nonNegative(definition.fadeInSeconds, 'Replay music fade-in duration');

  const tail = definition.tail;
  nonNegative(tail.durationSeconds, 'Replay music tail duration');
  nonNegative(tail.transitionSeconds, 'Replay music tail transition duration');
  nonNegative(tail.sustainSeconds, 'Replay music tail sustain duration');
  unitInterval(tail.gainScale, 'Replay music tail gain scale');
  unitInterval(tail.wetMix, 'Replay music tail wet mix');
  positive(tail.lowPassHz, 'Replay music tail low-pass frequency');
  positive(tail.wetLowPassHz, 'Replay music tail wet low-pass frequency');
  positive(tail.fadeLowPassHz, 'Replay music tail fade low-pass frequency');
  positive(tail.fadeWetLowPassHz, 'Replay music tail fade wet low-pass frequency');
  positive(tail.highPassHz, 'Replay music tail high-pass frequency');
  nonNegative(tail.preDelaySeconds, 'Replay music tail pre-delay');
  positive(tail.reverbSeconds, 'Replay music reverb duration');
  positive(tail.reverbDecay, 'Replay music reverb decay');
  if (tail.highPassHz >= tail.lowPassHz) {
    throw new Error('Replay music tail high-pass frequency must be below its low-pass frequency.');
  }
  if (tail.fadeLowPassHz > tail.lowPassHz) {
    throw new Error('Replay music tail fade low-pass frequency cannot exceed its initial low-pass frequency.');
  }
  if (tail.fadeWetLowPassHz > tail.wetLowPassHz) {
    throw new Error('Replay music tail fade wet low-pass frequency cannot exceed its initial wet low-pass frequency.');
  }
  if (tail.highPassHz >= tail.fadeLowPassHz) {
    throw new Error('Replay music tail high-pass frequency must stay below its fade low-pass frequency.');
  }
  if (tail.transitionSeconds > tail.durationSeconds) {
    throw new Error('Replay music tail transition duration cannot exceed its total duration.');
  }
  if (tail.transitionSeconds + tail.sustainSeconds >= tail.durationSeconds) {
    throw new Error('Replay music tail transition and sustain durations must leave time for fade-out.');
  }
}

export function validateMusicTrackDefinition(track: MusicTrackDefinition): void {
  if (!track.id.trim()) throw new Error('Music track ID must not be empty.');
  if (!track.url.trim()) throw new Error(`Music track ${track.id} URL must not be empty.`);
  nonNegative(track.dropSeconds, `Music track ${track.id} drop timestamp`);
  positive(track.durationSeconds, `Music track ${track.id} duration`);
  unitInterval(track.gain, `Music track ${track.id} gain`);
  if (track.dropSeconds > track.durationSeconds) {
    throw new Error(`Music track ${track.id} drop occurs after the track duration.`);
  }
}

export function validateMusicTrackForReplay(
  track: MusicTrackDefinition,
  definition: ReplayMusicDefinition,
): void {
  validateMusicTrackDefinition(track);
  if (track.dropSeconds < definition.minimumPreDropSeconds) {
    throw new Error(
      `Music track ${track.id} needs at least ${definition.minimumPreDropSeconds.toFixed(3)}s before its drop.`,
    );
  }
  const postDropSeconds = track.durationSeconds - track.dropSeconds;
  if (postDropSeconds < definition.minimumPostDropSeconds) {
    throw new Error(
      `Music track ${track.id} needs at least ${definition.minimumPostDropSeconds.toFixed(3)}s after its drop.`,
    );
  }
}

function nonNegative(value: number, label: string): void {
  if (!Number.isFinite(value) || value < 0) throw new Error(`${label} must be a finite non-negative number.`);
}

function positive(value: number, label: string): void {
  if (!Number.isFinite(value) || value <= 0) throw new Error(`${label} must be a positive finite number.`);
}

function unitInterval(value: number, label: string): void {
  if (!Number.isFinite(value) || value < 0 || value > 1) throw new Error(`${label} must be between 0 and 1.`);
}
